import type { ReactNode } from 'react';
import { IconButton, iconButtonClassName } from '@strivelabs/ui-core/v2';

interface PanelHeaderButtonProps {
  /** Named to assistive technology, and shown as the hover title. */
  label: string;
  children: ReactNode;
  onClick?: () => void;
  /**
   * Renders a link instead of a button. For an action the browser has to
   * follow rather than one this app handles — a download the server named when
   * it signed the URL, which no click handler can reproduce.
   */
  href?: string;
}

/** The link form of the shared `IconButton` (borderless, `xs`). */
const LINK_CLASS = iconButtonClassName({ size: 'xs' });

/**
 * A control in a panel's 52px header strip: the shared borderless `IconButton`
 * at `xs` (28px).
 *
 * One definition for every glyph in that strip, whichever panel it belongs to
 * and whether it acts or navigates, so four controls in one header are four of
 * the same control rather than three of one and one of another.
 */
export function PanelHeaderButton({ label, children, onClick, href }: PanelHeaderButtonProps) {
  if (href !== undefined) {
    return (
      <a href={href} aria-label={label} title={label} className={LINK_CLASS}>
        {children}
      </a>
    );
  }

  return (
    <IconButton size="xs" aria-label={label} title={label} onClick={onClick}>
      {children}
    </IconButton>
  );
}
